import {
    ChangeDetectionStrategy,
    Component,
    booleanAttribute,
    computed,
    effect,
    inject,
    input,
    model,
    output,
    signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Combobox, ComboboxInput } from '@angular/aria/combobox';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { translated } from '@h20-shared/utils/translate-utils';
import {
    PulseFieldComponent,
    PulsePopupInputDirective,
    PulsePopupSelectionBase,
    providePulsePopupControl,
} from '../pulse-popup';

/** `[pulsePopupOptions]` must carry `multi`: the listbox reads it once, at construction. */
@Component({
    selector: 'pulse-multiselect',
    exportAs: 'pulseMultiselect',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ComboboxInput, TranslateModule, PulseFieldComponent, PulsePopupInputDirective],
    templateUrl: './pulse-multiselect.component.html',
    styleUrl: './pulse-multiselect.component.scss',
    hostDirectives: [
        {
            directive: Combobox,
            inputs: ['disabled'],
        },
    ],
    host: {
        class: 'pulse-multiselect',
    },
    providers: [providePulsePopupControl(() => PulseMultiselectComponent)],
})
export class PulseMultiselectComponent<T> extends PulsePopupSelectionBase<T, T[]> {
    readonly values = input<readonly T[]>([]);

    readonly valuesChange = output<T[]>();

    readonly searchText = model('');

    readonly searchable = input(true);

    readonly clearable = input(false);

    readonly clearLabel = input('');

    readonly selectedText = input('');

    readonly applyOnClose = input(false, { transform: booleanAttribute });

    protected readonly multiple = true;

    private readonly translate = inject(TranslateService);

    private readonly langChange = toSignal(this.translate.onLangChange);

    private held: T[] | undefined;

    private readonly heldFrom = signal<number | undefined>(undefined);

    protected readonly count = computed(() => this.selection().length);

    private readonly fieldText = computed(() => {
        this.langChange();
        const count = this.heldFrom() ?? this.count();
        if (count === 0) {
            return '';
        }
        const selectedText = this.selectedText();
        if (selectedText !== '') {
            return selectedText;
        }
        const key = count === 1 ? 'PulseMultiselect.SelectedOne' : 'PulseMultiselect.SelectedCount';
        return translated(this.translate, key, { count });
    });

    protected readonly popupId = computed(() => this.combobox._pattern.popupId());

    protected readonly activeOptionId = computed(() => this.combobox._pattern.activeDescendant());

    constructor() {
        super();
        this.setReadonly();
        this.followInput(this.values);

        effect(() => {
            this.selection();
            this.inputElement().nativeElement.value = this.fieldText();
        });
    }

    protected override commit(values: readonly T[]): void {
        if (this.applyOnClose() && this.expanded() && this.heldFrom() === undefined) {
            this.heldFrom.set(this.count());
        }
        super.commit(values);
    }

    protected override notify(values: T[]): void {
        if (this.applyOnClose() && this.expanded()) {
            this.held = values;
            return;
        }
        super.notify(values);
    }

    protected override onClosed(): void {
        const held = this.held;
        this.heldFrom.set(undefined);
        if (held !== undefined) {
            this.held = undefined;
            super.notify(held);
        }
        if (this.searchText() !== '') {
            this.searchText.set('');
        }
        const panel = this.panel().nativeElement;
        if (panel.contains(panel.ownerDocument.activeElement)) {
            this.inputElement().nativeElement.focus();
        }
    }

    protected openField(event: MouseEvent): void {
        const input = this.inputElement().nativeElement;
        if (event.target !== input) {
            input.click();
        }
    }

    // One button: swapping in another would drop focus and close the panel.
    protected toggleAll(): void {
        if (this.count() > 0) {
            this.clearSelection();
            return;
        }
        this.commit(
            this.options()
                .filter((option) => !option.disabled())
                .map((option) => option.value())
        );
        this.syncListbox();
    }

    protected clear(event: MouseEvent): void {
        event.stopPropagation();
        this.clearSelection();
        this.inputElement().nativeElement.focus();
    }

    protected onSearch(event: Event): void {
        const target = event.target;
        if (target instanceof HTMLInputElement) {
            this.searchText.set(target.value);
        }
    }

    // The combobox handles Enter and Space on its host and would toggle the active option.
    protected keepKey(event: KeyboardEvent): void {
        if (event.key === 'Enter' || event.key === ' ') {
            event.stopPropagation();
        }
    }

    protected toModel(values: readonly T[]): T[] {
        return [...values];
    }

    protected toValues(values: readonly T[] | null): readonly T[] {
        return values ?? [];
    }

    protected emitChange(values: T[]): void {
        this.valuesChange.emit(values);
    }
}
